import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box, Button } from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNew";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import EmptyStatePlaceholder from "../../../components/feedback/EmptyStatePlaceholder";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import colors from "../../../theme/colors";
import { useBorrowerSubmissionsHook } from "../../../hooks/useBorrowerSubmissionsHook";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
// LENDMARQ DIVERGENCE: this app's own file hook, used directly — tpo has no
// equivalent upload-with-associations route, so there's no bridge in src/tpo.
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { useFillViewportHeight } from "../../../hooks/useFillViewportHeight";
import AssignLoanDialog from "./AssignLoanDialog";
import CreateLoanDialog from "./CreateLoanDialog";
import SubmissionDetailPanel from "./SubmissionDetailPanel";
import SubmissionsToolbar from "./SubmissionsToolbar";
import ShareableLinksModal from "../../../components/documents/ShareableLinksModal";
import { SUBMISSION_TYPES } from "./tableChips";
import T from "../submissionTableTokens";
import formatDate from "../../../_src_shared/utils/functions/formatDate";
import {
  buildSubmissionColumns,
  isGroupRow,
  submissionsDefaultColDef,
} from "../submissionColumns";
import { submissionPdfUploadPayload } from "../submissionPdf";
import { useFetchFullSubmission } from "../useFullSubmission";
import { matchLoanBorrower } from "../submissionLoanSuggestions";
import {
  GROUP_SORTS,
  LANES,
  LANE_META,
  SCOPES,
  buildBorrowerGroups,
  byNewestSubmission,
  loanDrilldownHref,
  submissionBorrowerName,
  submissionEmail,
  submissionEntityName,
  submissionIsAssigned,
  submissionIsMine,
  submissionOwnerId,
  submissionPropertyAddress,
} from "../submissionHelpers";

// Stable identity: AG Grid treats getRowId as immutable, and a fresh function
// each render makes it rebuild row nodes — which collapses any expanded detail.
const getSubmissionRowId = (p) => p.data._id;

// Group headers are ordinary rows (see submissionColumns) — they just can't be
// expanded as master/detail, and they sit shorter than a two-line submission.
const isRowMaster = (data) => !isGroupRow(data);
const GROUP_ROW_HEIGHT = 46;
const getRowHeight = (p) => (isGroupRow(p.data) ? GROUP_ROW_HEIGHT : undefined);

/**
 * Group headers get a tint so they read as a band across the table; an expanded
 * row gets the same blue as the detail panel below it, so the two form one
 * block. Inline styles win over `.ag-row-hover`, which is what keeps an open row
 * blue while the pointer is over it.
 */
const getRowStyle = (p) => {
  if (isGroupRow(p.data)) return { background: T.groupBg };
  return p.node.expanded ? { background: T.expandedBg } : undefined;
};

// AG Grid consults getRowStyle only when a row is painted, and expanding doesn't
// repaint the master row on its own. Repaint just that row — a full redrawRows()
// would remount every open detail panel and throw away its selected tab.
const handleRowGroupOpened = (e) => e.api.redrawRows({ rowNodes: [e.node] });

/**
 * The submissions queue, ported from the lendmarq-los applications queue: one
 * table over both submission kinds with a lane toggle, rather than two separate
 * tables. Both lists are fetched once and filtered client-side, so switching
 * lanes never refetches.
 *
 * Four lanes, two of which span both kinds:
 *   All          — every submission of either kind, one row each
 *   By Borrower  — the same set collapsed to one row per person, rolled up
 *   Loan Apps / Credit Auths — a single kind, one row each
 *
 * The lane is driven by the route (/submissions, /submissions/by-borrower,
 * /submissions/applications, /submissions/credit-auths) so the nav dropdown
 * deep-links straight into a lane and the URL survives a reload. Picking a chip
 * navigates.
 *
 * A submission is "assigned" once the backend has stamped it with a top-level
 * `loanNumber` / `dealId`. Unassigned rows carry the two actions that resolve
 * that — both live in the expanded row panel.
 */
const SubmissionsList = ({ lane, initialGrouped = false }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector((state) => state.user);
  const { getApplications, getCreditAuthorizations, loading } =
    useBorrowerSubmissionsHook();
  const { createLoanFromSubmission, assignSubmissionToLoan } =
    useUnderwritingHook();
  const { uploadLoanDocsWithAssociations } = useLoanFilesHook();
  // Every PDF this screen generates renders from the unredacted document, not
  // from the grid row — see attachSubmissionPdf.
  const fetchFullSubmission = useFetchFullSubmission();
  // `bottomGap: 0` — the table runs to the bottom edge of the screen rather
  // than stopping short of it, so the viewport is all table.
  const [gridRef, gridHeight] = useFillViewportHeight({
    minHeight: 380,
    bottomGap: 0,
  });

  // The feature hooks rebuild their functions on every render. Reading them
  // through a ref keeps the row handlers — and therefore
  // `detailCellRendererParams` — referentially stable, so an unrelated re-render
  // (the notification badge poll, a keystroke) can't make AG Grid rebuild the
  // detail rows and collapse whatever is open.
  const apiRef = useRef({});
  apiRef.current = { createLoanFromSubmission, assignSubmissionToLoan };

  const [applications, setApplications] = useState([]);
  const [creditAuths, setCreditAuths] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState(SCOPES.ALL);
  const [mineOnly, setMineOnly] = useState(false);
  // Grouping is view state, not a route — it composes with every type filter, so
  // it can't live in the lane. `/submissions/by-borrower` seeds it on.
  const [grouped, setGrouped] = useState(initialGrouped);
  const [groupSort, setGroupSort] = useState(GROUP_SORTS.RECENT);
  // Tracks which groups are OPEN, so the default (an empty set) is everything
  // collapsed — grouping is for scanning borrowers, and opening every one of
  // them defeats that.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [assignTarget, setAssignTarget] = useState(null);
  const [createTarget, setCreateTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [failedKinds, setFailedKinds] = useState(() => new Set());
  const [shareableOpen, setShareableOpen] = useState(false);

  const currentUserId = currentUser?._id ? String(currentUser._id) : null;
  const currentUserName = [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean)
    .join(" ");

  const toggleGroup = useCallback((key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Tag each row with its kind on the way in — in the All lane the Type column
  // and the detail panel both need to know which sort of submission a row is.
  const tag = (list, type) =>
    (Array.isArray(list) ? list : []).map((r) => ({ ...r, __type: type }));

  /**
   * The two lists are independent reads, and ONE of them failing used to be
   * invisible: the other kind still rendered, so a lane whose read 500'd looked
   * like a lane with nothing in it. `failedKinds` keeps which one broke so the
   * lane can say so rather than showing an empty queue — a borrower's
   * submission missing from the table and a submission that never arrived are
   * not the same problem and must not look the same.
   *
   * Extracted from the mount effect so the header's Refresh can re-run it: both
   * lists are read once per mount, so a submission that lands while the queue
   * is open never appears on its own.
   */
  const load = useCallback(() => {
    let done = 0;
    const failedNow = new Set();
    const settle = () => {
      done += 1;
      if (done === 2) {
        setFailedKinds(failedNow);
        setLoaded(true);
      }
    };
    setLoaded(false);
    getApplications({
      onSuccessFn: (d) =>
        setApplications(tag(d, SUBMISSION_TYPES.LOAN_APPLICATION)),
      onFailFn: () => failedNow.add(SUBMISSION_TYPES.LOAN_APPLICATION),
      onCompleteFn: settle,
    });
    getCreditAuthorizations({
      onSuccessFn: (d) =>
        setCreditAuths(tag(d, SUBMISSION_TYPES.CREDIT_AUTHORIZATION)),
      onFailFn: () => failedNow.add(SUBMISSION_TYPES.CREDIT_AUTHORIZATION),
      onCompleteFn: settle,
    });
    // Both fetchers are recreated each render, so they can't be dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Newest first, across both kinds. The two lists arrive separately, so
  // concatenating them alone would put every application ahead of every credit
  // authorization in the combined lanes — interleaving them by date is what
  // makes All read as one queue rather than two stacked ones.
  const allRows = useMemo(
    () => [...applications, ...creditAuths].sort(byNewestSubmission),
    [applications, creditAuths],
  );

  // Reflect an assignment locally so the row flips without a full refetch.
  const stampAssignment = useCallback((submissionId, fields) => {
    const patch = (prev) =>
      prev.map((r) => (r._id === submissionId ? { ...r, ...fields } : r));
    setApplications(patch);
    setCreditAuths(patch);
  }, []);

  // Opens the confirmation. The create itself is handleConfirmCreate below —
  // this only asks, because creating a loan isn't undoable from this screen.
  const handleCreateLoan = useCallback((row) => setCreateTarget(row), []);

  /**
   * File the submission's own PDF onto the loan it was just assigned to, tagged
   * to that loan's matching borrower.
   *
   * Why this is needed at all: `assign_to_deal` only pushes the submission body
   * into the deal's `creditAuthorizations` / `loanApplications` array. Nothing
   * lands in the loan's documents, so the signed authorization a processor
   * actually needs to look at isn't in the file list — it has to be downloaded
   * from this queue and re-uploaded by hand. This does that step.
   *
   * AWAITED by the assign flow, so the dialog stays open and disabled until the
   * upload and its associations have actually landed — closing on the assign
   * response alone would report "done" while a request was still in flight.
   *
   * It resolves either way and never throws: a failed attachment is a warning
   * the user can act on (download + upload by hand), not a reason to make an
   * assignment that already succeeded look broken.
   *
   * The document is rendered from the FULL submission, fetched by id — the grid
   * row it's handed has SSN, date of birth and the signature stripped by the
   * list read, and a loan file missing them is the one place that actually
   * matters: it's the copy underwriting works from, and nothing in it would say
   * the fields were withheld rather than never given.
   */
  const attachSubmissionPdf = useCallback(
    async (row, loan) => {
      const loanId = loan._id ?? loan.dealId;
      if (!loanId) return;

      try {
        const full = await fetchFullSubmission(row);
        const files = await submissionPdfUploadPayload(full);
        if (!files) return;

        // The submission's borrower is matched against borrowers the loan
        // ALREADY has — assign_to_deal doesn't copy one over. No match just
        // means the PDF is filed against the loan without a borrower tag.
        const borrower = matchLoanBorrower(full, loan);

        await uploadLoanDocsWithAssociations({
          loanId,
          files,
          borrowerIds: borrower?._id ? [borrower._id] : undefined,
          // `null`, not `undefined` — the hook defaults this parameter to
          // "File(s) Uploaded", and a default only applies to `undefined`. The
          // message below says what actually happened, including the borrower.
          onSuccessMsg: null,
          onSuccessFn: () =>
            enqueueSnackbar(
              borrower
                ? `${LANE_META[row.__type].label} PDF filed on loan #${loan.loanNumber} and tagged to ${submissionBorrowerName(borrower) || "the borrower"}.`
                : `${LANE_META[row.__type].label} PDF filed on loan #${loan.loanNumber}. No matching borrower on the loan, so it isn't tagged to one.`,
              { variant: borrower ? "success" : "info" },
            ),
          onFailFn: () =>
            enqueueSnackbar(
              `Assigned, but the ${LANE_META[row.__type].label} PDF couldn't be attached. Download it from this row and upload it to the loan.`,
              { variant: "warning" },
            ),
        });
      } catch (e) {
        enqueueSnackbar(
          `Assigned, but the ${LANE_META[row.__type].label} PDF couldn't be generated. Download it from this row and upload it to the loan.`,
          { variant: "warning" },
        );
      }
    },
    [enqueueSnackbar, fetchFullSubmission, uploadLoanDocsWithAssociations],
  );

  const handleConfirmAssign = useCallback(
    (loan) => {
      const row = assignTarget;
      if (!row || !loan) return;
      setBusyId(row._id);
      apiRef.current.assignSubmissionToLoan({
        submissionKind: LANE_META[row.__type].assignKind,
        loanNumber: loan.loanNumber,
        submission: row,
        onSuccessFn: async () => {
          stampAssignment(row._id, {
            dealId: loan._id ?? loan.dealId ?? null,
            loanNumber: loan.loanNumber ?? null,
          });
          enqueueSnackbar(`Assigned to loan #${loan.loanNumber}.`, {
            variant: "success",
          });
          // Both kinds: the payload builder tags each with its own canonical
          // docType under "Borrower Documents".
          await attachSubmissionPdf(row, loan);
          // Only now — the dialog stays open and disabled for the whole
          // sequence, so it never reports done with a request still in flight.
          setAssignTarget(null);
          setBusyId(null);
        },
        // NOT onCompleteFn: useAxiosHook runs that in a `finally` straight after
        // onSuccessFn, which returns its promise immediately — so clearing busy
        // there would release the dialog while the upload was still running.
        onFailFn: () => setBusyId(null),
      });
    },
    [assignTarget, attachSubmissionPdf, enqueueSnackbar, stampAssignment],
  );

  /**
   * Create a brand-new loan from the submission.
   *
   * The POST carries no body — create_deal_from_submission does the whole job
   * server-side (loan from the submission, borrower and subject properties
   * copied on, originator from the caller's company, permitted user added,
   * uploaded files carried across, submission stamped with the new dealId /
   * loanNumber). So there is nothing for this to assemble; it confirms, fires,
   * and reflects the result.
   *
   * Same close discipline as the assign flow: the dialog stays open and disabled
   * until the PDF attach has finished too.
   */
  const handleConfirmCreate = useCallback(() => {
    const row = createTarget;
    if (!row) return;
    setBusyId(row._id);
    apiRef.current.createLoanFromSubmission({
      submissionType: LANE_META[row.__type].createParam,
      submissionId: row._id,
      onSuccessFn: async (created) => {
        stampAssignment(row._id, {
          dealId: created?._id ?? null,
          loanNumber: created?.loanNumber ?? null,
        });
        enqueueSnackbar(
          created?.loanNumber != null
            ? `Loan #${created.loanNumber} created from this submission.`
            : "New loan created from this submission.",
          { variant: "success" },
        );
        // The created loan comes back with the submission's borrower already
        // copied onto it, so the PDF lands tagged to them — unlike the assign
        // path, where it can only match a borrower the loan already had.
        if (created) await attachSubmissionPdf(row, created);
        setCreateTarget(null);
        // Creating from inside the assign dialog resolves that dialog too.
        setAssignTarget(null);
        setBusyId(null);
      },
      onFailFn: () => setBusyId(null),
    });
  }, [createTarget, attachSubmissionPdf, enqueueSnackbar, stampAssignment]);

  const handleOpenLoan = useCallback(
    (row) => {
      const href = loanDrilldownHref(row.dealId);
      if (href) navigate(href);
    },
    [navigate],
  );

  const matchesQuery = useCallback(
    (r, q) =>
      [
        submissionBorrowerName(r),
        submissionEmail(r),
        submissionEntityName(r),
        submissionPropertyAddress(r),
        r.loanNumber,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    [],
  );

  // Everything that survives the four filters, still flat and newest-first.
  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      if (lane !== LANES.ALL && r.__type !== lane) return false;
      if (scope === SCOPES.ASSIGNED && !submissionIsAssigned(r)) return false;
      if (scope === SCOPES.UNASSIGNED && submissionIsAssigned(r)) return false;
      if (mineOnly && !submissionIsMine(r, currentUserId)) return false;
      return !q || matchesQuery(r, q);
    });
  }, [allRows, lane, query, scope, mineOnly, currentUserId, matchesQuery]);

  /**
   * What the grid actually renders.
   *
   * Ungrouped, that's `matching` as-is. Grouped, each borrower gets a header row
   * inserted above their submissions, and the children are stamped with the
   * display fields the Borrower cell needs in that mode — the cell can't derive
   * "am I inside a group" from the submission alone.
   *
   * Groups start collapsed — a header carries its own rollup, so the closed
   * state is already informative, and opening all of them would just be the
   * ungrouped list with headers wedged into it.
   */
  const visible = useMemo(() => {
    if (!grouped) return matching;

    return buildBorrowerGroups(matching, { sortBy: groupSort }).flatMap((g) => {
      const open = expandedGroups.has(g.key);
      const header = {
        __group: true,
        _id: `group:${g.key}`,
        __open: open,
        key: g.key,
        name: g.name,
        email: g.email,
        apps: g.apps,
        auths: g.auths,
        onLoans: g.onLoans,
        total: g.total,
      };
      if (!open) return [header];
      return [
        header,
        ...g.rows.map((r) => ({
          ...r,
          __inGroup: true,
          __submittedLabel: formatDate(r.submission_date, "—", "MMM D, YYYY"),
        })),
      ];
    });
  }, [grouped, groupSort, matching, expandedGroups]);

  // Counts describe the whole queue, not the current view — a pill that changed
  // its own count as you filtered couldn't tell you what switching to it holds.
  const laneCounts = useMemo(
    () => ({
      [LANES.ALL]: allRows.length,
      [SUBMISSION_TYPES.LOAN_APPLICATION]: applications.length,
      [SUBMISSION_TYPES.CREDIT_AUTHORIZATION]: creditAuths.length,
    }),
    [allRows, applications, creditAuths],
  );

  // Attribution comes from `owner_user_id`, inherited from the link the
  // submission arrived through. If nothing in the queue carries one, the filter
  // could only ever return an empty table — so it isn't offered.
  const canFilterMine = useMemo(
    () => Boolean(currentUserId) && allRows.some((r) => submissionOwnerId(r)),
    [allRows, currentUserId],
  );

  // Borrower · Type · Location · Submitted · Docs · Status — no actions column;
  // the row actions live in the expanded panel, same as the original queue. The
  // one exception is the Status chip on an unassigned row, which opens the
  // assign dialog directly (`setAssignTarget` is stable, so the defs don't
  // rebuild and collapse open detail rows).
  const gridColumnDefs = useMemo(
    () =>
      buildSubmissionColumns({
        onAssign: setAssignTarget,
        onCreateLoan: handleCreateLoan,
        onToggleGroup: toggleGroup,
        currentUserId,
        grouped,
      }),
    [handleCreateLoan, toggleGroup, currentUserId, grouped],
  );

  const defaultColDef = useMemo(
    () => submissionsDefaultColDef(grouped),
    [grouped],
  );

  const detailCellRendererParams = useMemo(
    () => ({
      onAssign: setAssignTarget,
      onCreateLoan: handleCreateLoan,
      onOpenLoan: handleOpenLoan,
      busyId,
    }),
    [handleCreateLoan, handleOpenLoan, busyId],
  );

  const meta = LANE_META[lane];

  // Only the kinds this lane actually shows: a failed credit-auth read is not
  // worth warning about while the broker is looking at the Loan apps lane.
  const missingKinds = useMemo(() => {
    const relevant =
      lane === LANES.ALL
        ? [
            SUBMISSION_TYPES.LOAN_APPLICATION,
            SUBMISSION_TYPES.CREDIT_AUTHORIZATION,
          ]
        : [lane];
    return relevant.filter((k) => failedKinds.has(k));
  }, [lane, failedKinds]);

  const filtered =
    Boolean(query.trim()) || scope !== SCOPES.ALL || mineOnly;

  return (
    // LENDMARQ DIVERGENCE: page gutters. tpo's dashboard shell pads its outlet;
    // this app's doesn't, so the header and grid sat flush against both edges.
    // `px: 3` matches the gutter the Correspondents / Users screens use.
    //
    // Capped and centred rather than full-bleed: on a wide monitor the seven
    // columns stretched to fill 2000px+, which put the Borrower and Status ends
    // of a row too far apart to read as one row.
    <Flx
      column
      gap={2}
      sx={{ px: 3, pt: 3, width: "100%", maxWidth: 1600, mx: "auto" }}
    >
      <Flx between centerY wrap gap={2}>
        <Flx column gap={0.25}>
          {/* One heading for all four lanes — the lane chips and the
              description below already say which slice you're looking at, and a
              title that changed under you made the lanes feel like four
              separate screens. */}
          <Txt component="div" variant="h2">
            Submissions
          </Txt>
          <Txt component="div" variant="body2" color="text.secondary">
            {meta.description}
          </Txt>
        </Flx>
        <Flx centerY wrap gap={1}>
          {meta.formPath && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 17 }} />}
              onClick={() =>
                window.open(meta.formPath, "_blank", "noopener,noreferrer")
              }
            >
              {meta.formButtonLabel}
            </Button>
          )}
          {/* Both lists are read once per mount, so a submission that lands
              while the queue is open doesn't appear on its own. */}
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon sx={{ fontSize: 17 }} />}
            disabled={loading}
            onClick={load}
          >
            Refresh
          </Button>
          {/* The two ways a link reaches a borrower, lifted from the send
              screen: the LO's permanent shareable links straight into a modal
              (a read — they are get-or-create per kind), and the targeted,
              prefilled, emailed link on /send. Send is the filled one: it is
              the create action of this header. */}
          <Button
            variant="outlined"
            startIcon={<LinkRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={() => setShareableOpen(true)}
          >
            Shareable links
          </Button>
          <Button
            variant="contained"
            startIcon={<SendRoundedIcon sx={{ fontSize: 17 }} />}
            onClick={() =>
              // The lane picks the kind: on the credit-auth lane the LO is
              // most likely after a credit auth. /send lets them change it.
              navigate(
                lane === LANES.CREDIT_AUTHORIZATION
                  ? "/send/credit-auth"
                  : "/send/loan-application",
                {
                  state: {
                    returnTo: { path: meta.path, label: "submissions" },
                  },
                },
              )
            }
          >
            Send link
          </Button>
        </Flx>
      </Flx>

      {missingKinds.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {missingKinds
            .map((k) => `${LANE_META[k].label.toLowerCase()}s`)
            .join(" and ")}{" "}
          could not be loaded, so any that exist are missing from this table.
          This is a failed read, not an empty queue.
        </Alert>
      )}

      <SubmissionsToolbar
        lane={lane}
        onLaneChange={navigate}
        counts={laneCounts}
        query={query}
        onQueryChange={setQuery}
        mineOnly={mineOnly}
        onMineOnlyChange={setMineOnly}
        grouped={grouped}
        onGroupedChange={setGrouped}
        groupSort={groupSort}
        onGroupSortChange={setGroupSort}
        scope={scope}
        onScopeChange={setScope}
        userName={currentUserName}
        canFilterMine={canFilterMine}
      />

      {/* The grid runs from here to the bottom of the viewport. */}
      <Box ref={gridRef}>
        {loaded && !loading && visible.length === 0 ? (
          <EmptyStatePlaceholder
            title={filtered ? "No matching submissions" : meta.emptyTitle}
            message={
              filtered
                ? "No submissions match the current search and filters. Try adjusting them."
                : meta.emptyMessage
            }
          />
        ) : (
          <AgGridTableWrapper
            columnDefs={gridColumnDefs}
            defaultColDef={defaultColDef}
            rowData={visible}
            getRowId={getSubmissionRowId}
            height={gridHeight}
            masterDetail
            // Paging cuts the row list at a fixed count, which in grouped mode
            // lands mid-group and strands a borrower's submissions on the next
            // page, away from their header. Grouped scrolls instead.
            pagination={!grouped}
            isRowMaster={isRowMaster}
            getRowHeight={getRowHeight}
            getRowStyle={getRowStyle}
            onRowGroupOpened={handleRowGroupOpened}
            detailCellRenderer={SubmissionDetailPanel}
            detailCellRendererParams={detailCellRendererParams}
            detailRowAutoHeight
            sx={{
              border: `1px solid ${colors.slate200}`,
              borderRadius: "12px",
              overflow: "hidden",
              // AG Grid v33's Theming API draws its own border on .ag-root-
              // wrapper and ignores the legacy `--ag-borders: none` the wrapper
              // sets, so the card border above was landing on top of it — two
              // lines a pixel apart. Suppress the grid's and keep the card's.
              "& .ag-root-wrapper": { border: "none", borderRadius: 0 },
              // agGroupCellRenderer nests the inner renderer inside its own
              // wrapper, which doesn't stretch — so a column-level cellStyle
              // can't center it. Stretch the wrapper and the value slot instead.
              "& .ag-cell-wrapper": { height: "100%", alignItems: "center" },
              "& .ag-cell-wrapper > .ag-group-value": {
                display: "flex",
                alignItems: "center",
                height: "100%",
                flex: 1,
                minWidth: 0,
              },
            }}
          />
        )}
      </Box>

      <AssignLoanDialog
        // The row itself, not a derived copy — the dialog memoizes its loan
        // suggestions off this prop, and a fresh object each render would
        // rescan the whole pipeline on every unrelated re-render.
        open={Boolean(assignTarget)}
        target={assignTarget}
        submissionLabel={
          assignTarget ? LANE_META[assignTarget.__type].label : ""
        }
        saving={Boolean(assignTarget) && busyId === assignTarget?._id}
        onClose={() => setAssignTarget(null)}
        onConfirm={handleConfirmAssign}
        // Stacks the confirmation over the assign dialog rather than replacing
        // it, so backing out of the create lands back on the loan list.
        onCreateLoan={() => setCreateTarget(assignTarget)}
      />

      <CreateLoanDialog
        open={Boolean(createTarget)}
        row={createTarget}
        saving={Boolean(createTarget) && busyId === createTarget?._id}
        onClose={() => setCreateTarget(null)}
        onConfirm={handleConfirmCreate}
      />

      {/* The LO's own permanent links for BOTH kinds, regardless of lane —
          they are per (owner, kind) and have nothing to do with which slice of
          the queue is on screen. */}
      <ShareableLinksModal
        open={shareableOpen}
        onClose={() => setShareableOpen(false)}
      />
    </Flx>
  );
};

export default SubmissionsList;
